import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GuideArticleSection,
  GuideKeyTakeaways,
  GuideLayout,
} from "@/components/guides/GuideLayout";
import { BarcodeFigure } from "@/components/guides/BarcodeFigure";
import { Callout } from "@/components/guides/Callout";
import { Compare } from "@/components/guides/Compare";
import { TryTool } from "@/components/guides/TryTool";
import { FaqSection } from "@/components/seo/FaqSection";
import { GUIDES_BY_SLUG } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const GUIDE = GUIDES_BY_SLUG["gs1-digital-link"];

const TOC = [
  { id: "the-short-answer", label: "The Short Answer" },
  { id: "anatomy", label: "Anatomy of a GS1 Digital Link URL" },
  { id: "path-vs-query", label: "Path AIs vs Query AIs" },
  { id: "resolvers", label: "Custom Resolver Domains" },
  { id: "rendering", label: "Rendering as QR or Data Matrix" },
  { id: "pos-extraction", label: "How POS Extracts the GTIN" },
  { id: "common-mistakes", label: "Common Mistakes" },
  { id: "quick-decision", label: "Quick Decision Guide" },
  { id: "resolver", label: "GS1 Digital Link Resolver" },
  { id: "standard", label: "GS1 Digital Link Standard and Specification" },
  { id: "references", label: "References" },
];

const FAQ = [
  {
    q: "Is a GS1 Digital Link Just a Normal QR Code?",
    a: "Visually, yes — it's a standard QR Code symbol that any phone camera reads. The difference is in the encoded value: a GS1 Digital Link's value is a URL whose path follows a GS1-defined grammar, so the same scan can serve a shopper's phone (opens as a URL) and a Sunrise-ready POS (extracts the GTIN deterministically).",
  },
  {
    q: "Do I Have to Use id.gs1.org?",
    a: "No. id.gs1.org is the canonical resolver that always works for any GS1-issued GTIN, and it's the safe default if you don't want to run anything yourself. Most brands eventually deploy their own resolver — a small web service that handles the same /01/<GTIN>/… path grammar at their own domain — so the URL on the pack carries their branding.",
  },
  {
    q: "Can I Encode Batch and Expiry Inside a Digital Link?",
    a: "Yes — the AIs you'd put in a GS1 Data Matrix go into the path or the query string. Batch (AI 10) is variable-length and goes in the path after the GTIN; date AIs (11/13/15/17) are data attributes and go in the query string, as in ?17=261231. The Digital Link standard lists which AIs are 'qualifiers' (allowed in the path) and which are 'attributes' (query string).",
  },
  {
    q: "How Does a Phone Scan Handle a Digital Link?",
    a: "Identically to any other URL QR — the phone camera recognises the QR, the OS extracts the URL, and the user lands on whatever page the resolver returns. Brands typically resolve a Digital Link to a product page on their own site, optionally enriched with batch / expiry context if those AIs were encoded.",
  },
  {
    q: "Can I Render a Digital Link as a Data Matrix Instead of a QR?",
    a: "Yes — the symbology is independent of the payload format. GS1 recommends QR for consumer-facing packs (because phone cameras handle QR best) and Data Matrix for industrial / pharma contexts where the symbol size matters more than the phone scan story. Either symbology carries the same Digital Link URL.",
  },
  {
    q: "Does a Digital Link Work Without Internet?",
    a: "The POS scan works without internet — the scanner extracts the GTIN from the URL path locally, no DNS or HTTP involved. The shopper's phone scan does need internet to open the URL, the same as any other QR that points to a web page.",
  },
  {
    q: "Is GS1 Digital Link Required for 2027?",
    a: "Not as a legal rule. Sunrise 2027 is an industry target for retail point-of-sale scanners to read 2D barcodes by the end of 2027, and a QR Code with GS1 Digital Link is one accepted option alongside GS1 DataMatrix and GS1 QR Code carrying element strings. Linear EAN and UPC barcodes are not withdrawn on that date. Retailers or regulated sectors can set their own deadlines, so confirm with your trading partners and your local GS1 office.",
  },
];

export const Route = createFileRoute("/barcode-guides/gs1-digital-link")({
  component: Gs1DigitalLinkGuide,
  head: () => {
    const base = pageHead({
      title: GUIDE.title,
      description: GUIDE.description,
      path: GUIDE.path,
      ogType: "article",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: GUIDE.title,
          description: GUIDE.description,
          mainEntityOfPage: absoluteUrl(GUIDE.path),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
          datePublished: GUIDE.lastUpdated,
          dateModified: GUIDE.lastUpdated,
          about: GUIDE.keyword,
        }),
        ldScript(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Barcode Guides", path: "/barcode-guides" },
            { name: GUIDE.shortTitle, path: GUIDE.path },
          ]),
        ),
      ],
    };
  },
});

function Gs1DigitalLinkGuide() {
  return (
    <GuideLayout
      guide={GUIDE}
      toc={TOC}
      nextRead={[
        {
          to: "/barcode-guides/sunrise-2027-explained",
          label: "Sunrise 2027 explained",
          desc: "The retail transition that makes Digital Link the consumer-pack default.",
        },
        {
          to: "/barcode-guides/1d-vs-2d-barcodes",
          label: "1D vs 2D barcodes",
          desc: "The capacity story behind moving from EAN-13 to a 2D Digital Link QR.",
        },
        {
          to: "/gs1-digital-link-generator",
          label: "GS1 Digital Link Generator",
          desc: "Build a Digital Link URL from a GTIN and AIs and render it as QR or Data Matrix.",
        },
        {
          to: "/gs1-digital-link-decoder",
          label: "GS1 Digital Link Decoder",
          desc: "Split a Digital Link URL into its GTIN, lot, serial and date fields.",
        },
      ]}
    >
      <p>
        Marketing wants one QR on the front of the new pack. It needs to open the product page on
        the brand site when a shopper scans it with their phone. IT wants a different QR on the back
        of the pack — the one carrying the GTIN that the Sunrise-ready till will scan. Procurement
        looks at both and asks if maybe the back-of-pack QR could be the front-of-pack QR, since
        that&apos;s one less thing to print. That&apos;s the conversation <strong>GS1 Digital Link</strong> exists to settle: a single 2D barcode whose value is a URL
        that the phone can open AND the POS can parse for a GTIN.
      </p>
      <p>
        This guide walks through how the URL is built, which Application Identifiers go in the path
        versus the query string, when a brand needs its own resolver, and how a Sunrise-ready POS
        actually pulls the GTIN out of what looks, to anyone else, like a normal web link.
      </p>

      <GuideArticleSection id="the-short-answer" title="The Short Answer">
        <p>
          A <strong>GS1 Digital Link</strong> is a URL whose path follows a GS1-defined grammar. The
          path segments encode an Application Identifier and its value — <code>/01/&lt;GTIN&gt;</code> for the GTIN itself, optionally followed by qualifiers such as <code>/10/&lt;batch&gt;</code> in the path and data attributes such as <code>?17=&lt;expiry&gt;</code> in the query string. Render that URL
          as a QR Code (the consumer default) or a Data Matrix (the industrial default) and the same
          symbol works as a shopper-facing URL and as a GS1 carrier for retail POS.
        </p>
        <BarcodeFigure
          format="qrcode"
          value="https://id.gs1.org/01/05012345678900/10/ABC123?17=261231"
          formatLabel="QR · GS1 Digital Link"
          caption={
            <>
              QR Code encoding a GS1 Digital Link URL — GTIN <code>05012345678900</code>, batch <code>ABC123</code>, expiry <code>2026-12-31</code>. Phone cameras open it as a URL;
              Sunrise-ready POS extracts the GTIN from the <code>/01/</code> path segment.
            </>
          }
        />
      </GuideArticleSection>

      <GuideArticleSection id="anatomy" title="Anatomy of a GS1 Digital Link URL">
        <p>Take the URL embedded in the QR above and walk through it piece by piece:</p>
        <figure>
          <pre>
            <code>{`https://id.gs1.org/01/05012345678900/10/ABC123?17=261231`}</code>
          </pre>
        </figure>
        <ul>
          <li>
            <code>https://id.gs1.org</code> — the <strong>resolver domain</strong>. Any
            HTTPS-capable hostname works; <code>id.gs1.org</code> is the canonical community-run
            resolver that returns brand-supplied content for any registered GTIN.
          </li>
          <li>
            <code>/01/05012345678900</code> — the <strong>primary key</strong>, here AI <code>01</code> (GTIN) followed by the 14-digit GTIN value; other keys such as SSCC (<code>00</code>) and GLN (<code>414</code>) take the same position. UPC-A and EAN-13 are
            left-padded to 14 digits when they sit in a Digital Link path.
          </li>
          <li>
            <code>/10/ABC123</code> — a <strong>qualifier</strong>: AI <code>10</code> (batch /
            lot), value <code>ABC123</code>. Qualifiers narrow the identity to a specific production
            batch.
          </li>
          <li>
            <code>?17=261231</code> — a <strong>data attribute</strong> in the query string: AI <code>17</code> (expiry date), <code>YYMMDD</code> encoded.
          </li>
        </ul>
        <p>
          The full grammar is in the <em>GS1 Digital Link standard</em>. The two things to remember
          in practice: a product URL puts <code>/01/&lt;GTIN&gt;</code> after the resolver host (and after any path the brand adds in front of it), key qualifiers follow as <code>/AI/value</code> pairs in a fixed order, and data attributes go in the query string.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="path-vs-query" title="Path AIs vs Query AIs">
        <p>
          Not every Application Identifier belongs in the path. GS1 splits AIs into two roles inside
          a Digital Link:
        </p>
        <Compare
          panes={[
            {
              label: "Path AIs (qualifiers)",
              caption: "AIs that narrow the identified product. Sit in the URL path.",
              children: (
                <ul>
                  <li>
                    <code>01</code> — GTIN (always first)
                  </li>
                  <li>
                    <code>10</code> — Batch / lot
                  </li>
                  <li>
                    <code>21</code> — Serial number
                  </li>
                  <li>
                    <code>235</code> — Third-party-controlled serial
                  </li>
                  <li>
                    <code>22</code> — Consumer product variant
                  </li>
                </ul>
              ),
            },
            {
              label: "Query AIs (data attributes)",
              caption: "AIs that carry per-unit data. Sit in the query string.",
              children: (
                <ul>
                  <li>
                    <code>11</code> — Production date
                  </li>
                  <li>
                    <code>13</code> — Packaging date
                  </li>
                  <li>
                    <code>15</code> — Best-before date
                  </li>
                  <li>
                    <code>17</code> — Expiry date
                  </li>
                  <li>
                    <code>240</code> — Additional product ID
                  </li>
                </ul>
              ),
            },
          ]}
        />
        <p>
          The split is part of the syntax, not a style choice: key qualifiers sit in the path in the
          order 22, 10, 21 after the GTIN, and data attributes such as dates go in the query string.
          A URL that puts a data attribute in the path is not a valid GS1 Digital Link URI, and a
          conformant parser can reject it.
        </p>
        <Callout variant="spec" title="Path or Query Is Fixed by the Standard">
          <p>
            The GS1 Digital Link standard assigns each AI a place: a primary key and its qualifiers
            in the path, data attributes in the query string as <code>AI=value</code> pairs joined
            by <code>&amp;</code>. Values that contain reserved characters such as <code>/</code> are
            percent-encoded rather than moved.
          </p>
        </Callout>
      </GuideArticleSection>

      <GuideArticleSection id="resolvers" title="Custom Resolver Domains">
        <p>
          The resolver domain is whatever lives between <code>https://</code> and the first path
          segment. <code>id.gs1.org</code> is the canonical resolver — it works for any GS1-issued
          GTIN and returns content registered by the brand owner. Many brands instead stand up their
          own resolver at their own domain.
        </p>
        <figure>
          <pre>
            <code>{`# Canonical GS1 resolver (always works)
https://id.gs1.org/01/05012345678900

# Brand-owned resolver (same grammar, owned domain)
https://yourbrand.com/p/01/05012345678900

# Either URL is a valid GS1 Digital Link — POS pulls
# 05012345678900 from the /01/ path segment in both.`}</code>
          </pre>
        </figure>
        <p>
          A brand-owned resolver is a thin web service that answers any <code>/01/&lt;GTIN&gt;…</code> request and either redirects to the right product page or
          returns the content directly. The work is in three parts: standing up the service, mapping
          every GTIN to a destination, and (for traceability) reading the AIs that arrived alongside
          the GTIN. Several SaaS vendors offer this off the shelf; the open-source <em>Digital Link Toolkit</em> from GS1 covers the parsing rules if you&apos;re building
          your own.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="rendering" title="Rendering as QR or Data Matrix">
        <p>
          The symbology is independent of the payload. Both QR Code and Data Matrix can carry a
          Digital Link URL of any length within their respective capacity limits.
        </p>
        <Compare
          panes={[
            {
              label: "QR — consumer default",
              caption: "Phone cameras decode QR natively. The right choice for front-of-pack.",
              children: (
                <BarcodeFigure
                  format="qrcode"
                  value="https://id.gs1.org/01/05012345678900/10/ABC123?17=261231"
                  formatLabel="QR · Digital Link"
                  caption={
                    <>
                      Same Digital Link URL rendered as QR — works on phone cameras, on
                      Sunrise-ready POS, and as a printable shape with built-in error correction.
                    </>
                  }
                />
              ),
            },
            {
              label: "Data Matrix — industrial default",
              caption: "Denser at small sizes. Use for pharma / electronics packaging.",
              children: (
                <BarcodeFigure
                  format="datamatrix"
                  value="https://id.gs1.org/01/05012345678900/10/ABC123?17=261231"
                  formatLabel="Data Matrix · Digital Link"
                  caption={
                    <>
                      Same URL as a Data Matrix — the carrier of choice when symbol size matters
                      more than the phone scan story.
                    </>
                  }
                />
              ),
            },
          ]}
        />
        <p>
          To prototype either form for proofing, use the <Link to="/gs1-digital-link-generator">GS1 Digital Link Generator</Link>. It builds the
          URL from a GTIN plus AIs and renders both the QR and the Data Matrix from the same
          payload.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="pos-extraction" title="How POS Extracts the GTIN">
        <p>
          When a Sunrise-ready POS scans a GS1 Digital Link QR, it doesn&apos;t open the URL. It
          parses the URL locally and reads the path:
        </p>
        <ol>
          <li>
            The scanner decodes the QR to a string. The string starts with <code>https://</code> (or <code>http://</code> for some older resolvers).
          </li>
          <li>
            The POS application strips the scheme and host, leaving a path like <code>/01/05012345678900/10/ABC123</code>.
          </li>
          <li>
            It walks the path in pairs: AI <code>01</code> means the next segment is a GTIN-14, AI <code>10</code> means the next segment is the batch, and so on. The grammar is
            deterministic, so no schema lookup is needed.
          </li>
          <li>
            The extracted GTIN goes into the same lookup pipeline that a 1D EAN feeds; the till
            posts the price, the back-office picks up the batch / expiry.
          </li>
        </ol>
        <p>
          To verify a URL parses cleanly, paste it into the decoder. The same input feeds into the
          same logic POS uses:
        </p>
        <TryTool
          to="/gs1-digital-link-decoder"
          label="GS1 Digital Link Decoder"
          description="Parse any GS1 Digital Link URL into structured GTIN + AI data."
          value="https://id.gs1.org/01/05012345678900/10/ABC123?17=261231"
          cta="Decode the worked example"
        />
      </GuideArticleSection>

      <GuideArticleSection id="common-mistakes" title="Common Mistakes">
        <p>
          Most broken Digital Links come from a few encoding errors that a test decode catches before the artwork goes to print.
        </p>
        <ul>
          <li>
            <strong>Confusing a marketing QR with a Digital Link.</strong> A QR opening <code>https://yourbrand.com/campaign</code> isn&apos;t a Digital Link — there&apos;s no <code>/01/</code> path segment for POS to grab a GTIN from. Both can sit on the pack;
            only the Digital Link form is Sunrise-ready.
          </li>
          <li>
            <strong>Padding the GTIN wrong.</strong> The path segment expects 14 digits. UPC-A (12
            digits) and EAN-13 (13 digits) are left-padded with zeros. Get the padding wrong and POS
            reads a different (or invalid) GTIN.
          </li>
          <li>
            <strong>Encoding dates in the wrong format.</strong> Date AIs use <code>YYMMDD</code> —
            six digits, no separators. Encoding <code>17=2026-12-31</code> instead of <code>17=261231</code> breaks the parse.
          </li>
          <li>
            <strong>Putting data attributes in the path.</strong> Dates, weights and other data
            attributes belong in the query string; written as path segments they make the URL
            invalid, and a conformant parser can reject it. Keep qualifiers in the path and data
            attributes in the query.
          </li>
          <li>
            <strong>Putting the resolver behind a redirect.</strong> A Digital Link URL needs to be
            the one printed on the pack. A redirect to a marketing URL works for the phone but loses
            the AI structure for POS.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="quick-decision" title="Quick Decision Guide">
        <p>
          For most brands the answer is the GS1 resolver, a QR Code on consumer packs and a decode test before press.
        </p>
        <ul>
          <li>
            <strong>Default resolver:</strong> <code>https://id.gs1.org/01/&lt;GTIN&gt;…</code>.
            Always works, costs nothing, perfectly Sunrise-compliant.
          </li>
          <li>
            <strong>Branded resolver:</strong> stand one up only if you want analytics, brand voice
            or to enrich the page with batch-aware content. Not required for Sunrise.
          </li>
          <li>
            <strong>Carrier choice:</strong> QR for any consumer-facing pack (phone scans matter).
            Data Matrix for pharma / industrial when symbol size dominates.
          </li>
          <li>
            <strong>Validate before press.</strong> Build the URL with the <Link to="/gs1-digital-link-generator">Digital Link Generator</Link>, decode it back
            with the <Link to="/gs1-digital-link-decoder">Digital Link Decoder</Link>, confirm POS
            will extract the right GTIN and AIs.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="resolver" title="GS1 Digital Link Resolver">
        <p>
          A GS1 Digital Link resolver is the web server at the URL&apos;s host name. It reads the GS1 identifiers in the request path and redirects the visitor to a page linked to that product, batch or item.
        </p>
        <ol>
          <li>The phone opens the URL and sends a request to the resolver host.</li>
          <li>The resolver reads the primary key, such as the GTIN, and any qualifiers such as the lot or serial number.</li>
          <li>It looks up the links the brand registered for that identifier and picks the default one, unless the request asks for a specific link type.</li>
          <li>It answers with an HTTP redirect, and the browser loads the target page.</li>
        </ol>
        <p>
          Link types let one code serve several destinations. Adding linkType=gs1:pip to the query string asks for the product information page, and other types in the GS1 Web Vocabulary cover destinations such as instructions or recall status.
        </p>
        <p>
          Because qualifiers are part of the request, a resolver can treat batches differently. A brand can send one recalled lot to a recall notice while every other lot of the same GTIN still opens the normal product page.
        </p>
        <p>
          Before testing a redirect, paste the URL into the <Link to="/gs1-digital-link-decoder">GS1 Digital Link decoder</Link> to see exactly which identifiers the resolver will receive.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="standard" title="GS1 Digital Link Standard and Specification">
        <p>
          The GS1 Digital Link standard defines how GS1 identification keys and Application Identifiers are written as web addresses. The AIs themselves, with their lengths and data formats, come from the GS1 General Specifications.
        </p>
        <p>
          GS1 publishes the GS1 Digital Link syntax and the rules for conformant resolvers as separate documents, and refines details between editions, so check the current release on gs1.org before you build a parser or a resolver. The syntax document sets out:
        </p>
        <ul>
          <li>Which GS1 keys can start the path, including the GTIN (01), SSCC (00) and GLN (414).</li>
          <li>Which qualifiers may follow each key, and in which order.</li>
          <li>Which AIs are data attributes that belong in the query string.</li>
          <li>How reserved characters inside a value must be percent-encoded.</li>
        </ul>
        <h3>GS1 Digital Link Format</h3>
        <p>
          The GS1 Digital Link format puts the parts in a fixed sequence: scheme and host, an optional path of the brand&apos;s own, the primary key, its qualifiers, then the query string.
        </p>
        <figure>
          <pre>
            <code>{`https://example.com/01/09506000134352/10/ABC1/21/12345?17=271231`}</code>
          </pre>
        </figure>
        <p>
          For a GTIN, the qualifier order is consumer product variant (22), then batch or lot (10), then serial number (21). Any of them can be left out, but the order cannot change, and a URL with only the GTIN is already complete.
        </p>
        <p>
          A QR code with GS1 Digital Link encodes this whole string unchanged. The <Link to="/gs1-digital-link-generator">GS1 Digital Link generator</Link> follows this layout, placing lot and serial in the path and expiry, best-before and net weight in the query string.
        </p>
      </GuideArticleSection>

      <GuideKeyTakeaways
        items={[
          "A GS1 Digital Link is a URL whose path follows a GS1 grammar — one 2D barcode serves the shopper's phone and the Sunrise-ready POS.",
          "Path AIs (qualifiers) identify the product variant; query AIs (data attributes) carry per-unit data like expiry.",
          "id.gs1.org is the default resolver; a branded resolver is optional and doesn't change Sunrise compatibility.",
          "QR is the consumer default; Data Matrix is the industrial default — both carry the same Digital Link URL.",
          "Validate every Digital Link payload with the decoder before sending artwork to press — the URL grammar is unforgiving once it's been printed.",
        ]}
      />

      <GuideArticleSection id="references" title="References">
        <ul>
          <li>
            GS1 Digital Link standard — the canonical document defining the URL grammar, qualifier /
            attribute split and resolver behaviour. Published by <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              GS1
            </a>
            .
          </li>
          <li>
            GS1 General Specifications — the AI table that the path segments draw from: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
          <li>
            GS1 &quot;2D at POS&quot; — Sunrise 2027 milestones that make Digital Link the default
            consumer-pack 2D carrier: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
        </ul>
      </GuideArticleSection>

      <p>
        Need to ship a Digital Link tomorrow? Build one with the <Link to="/gs1-digital-link-generator">GS1 Digital Link Generator</Link>, decode it with the <Link to="/gs1-digital-link-decoder">GS1 Digital Link Decoder</Link>, and match the Sunrise
        context in the <Link to="/barcode-guides/sunrise-2027-explained">Sunrise 2027 page</Link>.
      </p>

      <FaqSection heading="Frequently Asked Questions" items={FAQ} />
    </GuideLayout>
  );
}
